/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { Store } from '@ngrx/store';
import { IAppShellFacade } from '@hcworkspace/sysque/toolbelt/data-access';
import {
  CheckInvalidShortcuts,
  InitializeHangerSpecifications,
  InitializeRootPath,
  InitializeToolbeltAccordionGroups,
  ShowSysquePopupAction,
  sendPopupRoutingAction,
  setAddFittingModeAction,
} from '../toolbelt.actions';
import { Observable, forkJoin, from, of } from 'rxjs';
import { showAddFittingResultPanelAction } from '../curate-search/curate-search-actions';
import { LocalStorageService } from '../../../../../../toolbelt/utils/src/lib/local-storage/local-storage.service';
import { ToolGroup } from '../../models/tool-item-group';
import {
  selectAddFittingMode,
  selectInvalidShortcut,
  selectSysquePopup,
  selectToolBeltItems,
} from '../tool-belt.selector';
import { selectShowAddFittingResultPanel } from '../curate-search/curate-search-selector';
import { BackendService } from '../../../../../utils/src/lib/sysque-backend/backend.service';
import { DownloadFamiliesReturnCodes } from '../../models/download-families-return-codes';
import { ElectricalSettingsReturnCodes } from '../../models/electrical-settings-return-codes';
import { DockableReceiveController } from '../../receive-controllers/dockable-receive-controller';
import { WorksharingService } from '../../../../../utils/src/lib/sysque-worksharing/sysque-worksharing.service';
import { ProcessingService } from '../../../../../utils/src/lib/sysque-processing/sysque-processing.service';
import { DockableMessagesService } from '../../../../../utils/src/lib/sysque-dockable-messages/sysque-dockable-messages.service';
import { AcrylicSurfaceService } from '../../acrylic-surface/acrylic-surface.service';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { GlobalConstants } from '../../constants/global-constants';
import { SysquePopupConfig } from '../../models/sysque-popup-config';

@Injectable({
  providedIn: 'root',
})
export class AppShellFacade implements IAppShellFacade {
  constructor(
    public store: Store,
    public localStorageService: LocalStorageService,
    public readonly receiveMessageHandler: ReceiveMessageHandler,
    public backendService: BackendService,
    private worksharingService: WorksharingService,
    private processingService: ProcessingService,
    private dockableMessagesService: DockableMessagesService,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {
    this.receiveMessageHandler.registerController(
      new DockableReceiveController(this.localStorageService, this.worksharingService, this.processingService, this.dockableMessagesService, this.acrylicSurfaceService)
    );
  } 

  public dispatchShowSysquePopupAction(action: any): void {
    this.store.dispatch(ShowSysquePopupAction(action));
  }

  public selectSysquePopup(): Observable<SysquePopupConfig> {
    return this.store.select(selectSysquePopup);
  }

  public sendPopupRoutingAction(itemName: any, width: number, height: number): void {
    this.store.dispatch(sendPopupRoutingAction({ route: itemName, width: width, height: height }));
  }

  public selectShowAddFittingResultPanel(): Observable<boolean> {
    return this.store.select(selectShowAddFittingResultPanel);
  }

  public selectAddFittingMode(): Observable<boolean> {
    return this.store.select(selectAddFittingMode);
  }

  public selectToolBeltItems(): Observable<ToolGroup[]> {
    return this.store.select(selectToolBeltItems);
  }

  public getStorage(key: string): string | SysqueElectricalState {
    return this.localStorageService.getStorage(key);
  }

  public onDoneAction(): void {
    this.store.dispatch(setAddFittingModeAction({ mode: false }));
    this.store.dispatch(showAddFittingResultPanelAction({ mode: false }));
  }

  public updateStorage(key: string, value: string): void {
    this.localStorageService.updateStorage(key, value);
  }

  public initializeData(): void {
    const data = [
      of(this.InitializeToolbeltAccordionGroups()),
      of(this.InitializeHangerSpecifications()),
      of(this.InitializeRootPath()),
    ];
    forkJoin(data);
  }

  public InitializeToolbeltAccordionGroups(): void {
    this.store.dispatch(InitializeToolbeltAccordionGroups());
  }

  public InitializeHangerSpecifications(): void {
    this.store.dispatch(InitializeHangerSpecifications());
  }

  public InitializeRootPath(): void {
    this.store.dispatch(InitializeRootPath());
  }

  public downloadMissingFamilies(): Observable<boolean> {
    return this.backendService.downloadMissingFamilies();
  }

  public checkInvalidShortcuts(): Observable<ElectricalSettingsReturnCodes> {
    this.store.dispatch(CheckInvalidShortcuts());
    return this.store.select(selectInvalidShortcut);
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateDispatchLocalStorage(key, value);
  }

  public updateGenericFittings(): Observable<DownloadFamiliesReturnCodes>{
    return this.backendService.updateGenericFittings();
  }

  public checkIfMissingFamilies(): Observable<DownloadFamiliesReturnCodes> {
    return from<Promise<DownloadFamiliesReturnCodes>>(this.backendService.messageHandler.sendRequestAsync(GlobalConstants.CheckMissingFamilies));
  }

  public getLocalStorage(key: string): any {
    return this.localStorageService.getStorage(key);
  }
}